import { AI_VIDEO_CONTINUITY_FAQ } from '../../content/aiVideoContinuityPage';
import { PIPELINE_MIGRATE as SHOT_CONTROL_PIPELINE_MIGRATE } from '../../content/productShotControlPage';
import { PageFaqSection } from '../ds';
import { CDN } from '../../lib/cdn';
import { ProductFinalCta } from '../product/ProductFinalCta';
import { ProductHeroHeader } from '../product/ProductHeroHeader';
import { ProductMigrate } from '../product/ProductMigrate';

export function AiVideoContinuitySections() {
  return (
    <div className="product-page">
      <ProductHeroHeader
        label="Use Case - AI Video Continuity"
        title={
          <>
            Continuity, Held Across
            <br />
            Every Shot. And <em>Verified.</em>
          </>
        }
        sub="Keep characters, wardrobe, and locations consistent across every AI-generated shot. Induce’s Project Memory Graph holds them, and the Coherence Verifier checks every frame against it before your cut."
        note="Project Memory Graph · No reference uploads · Verified on every shot"
        still={{
          kind: 'diner',
          slug: 'CONTINUITY',
          scene: 'USE CASE / 01',
          genre: 'MEMORY',
          sid: 'uc-continuity-hero',
          videoSrc: CDN.videos.continuity,
          alt: 'AI video continuity demo — consistent characters across shots',
        }}
      />

      <section className="answer band-light" aria-labelledby="uc-cont-problem" id="the-problem">
        <div className="container">
          <div className="content-measure">
            <div className="tag">- The problem</div>
            <div className="answer-body">
              <h2 id="uc-cont-problem" className="a-lead">
                <b>Consistency is claimed by everyone. It’s checked by no one.</b>
              </h2>
              <p className="a-body">
                Every tool promises consistent characters — inside one generation. Across a timeline, faces
                shift and wardrobe drifts, and reference uploads only patch it shot by shot. Induce keeps
                consistency in the Project Memory Graph and verifies every shot against it.
              </p>
              <div className="a-stats reveal">
                <div className="a-stat">
                  <b>∞</b>
                  <span>Shots sharing the same character memory</span>
                </div>
                <div className="a-stat">
                  <b>5+</b>
                  <span>Entity classes tracked per project</span>
                </div>
                <div className="a-stat">
                  <b>50</b>
                  <span>Film-grammar rules checked per shot</span>
                </div>
                <div className="a-stat">
                  <b>0</b>
                  <span>Manual intervention required</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="band-dark" id="memory-graph">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- What the memory graph holds</span>
            <h2>
              Everything in your film, held in <em>one graph.</em>
            </h2>
            <p className="sub">
              Characters, wardrobe, locations, props, and shots as entities — continuity as the edges between
              them.
            </p>
          </div>
          <div className="step-flow reveal">
            {[
              [
                '01',
                'Characters: face, build, and state',
                'Every named character gets a persistent node: physical description, distinctive features, and narrative state at any point in the timeline.',
                'People',
              ],
              [
                '02',
                'Wardrobe: outfit per scene, transitions tracked',
                "Characters are dressed for the scene they're in, not the first scene they appeared in.",
                'Wardrobe',
              ],
              [
                '03',
                'Locations: environment, time, and condition',
                'The precinct in scene 3 is the same precinct in scene 28 — same layout, same light quality.',
                'Locations',
              ],
              [
                '04',
                'Props: objects that carry narrative weight',
                "The envelope handed over in scene 4 appears in the same character's hand in scene 12.",
                'Props',
              ],
              [
                '05',
                'Mood: tone and colour across the cut',
                'Scene-level emotional register is tracked as a graph property and applied to every shot in that scene.',
                'Mood',
              ],
            ].map(([n, title, body, tag]) => (
              <div key={n} className="step-item">
                <div className="step-n">{n}</div>
                <div className="step-body">
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
                <div className="step-tag">{tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band-light" id="update-propagation">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Update propagation</span>
            <h2>
              Continuity you control across <em>all your visuals.</em>
            </h2>
          </div>
          <div className="cap-grid reveal">
            {[
              [
                'Traditional AI video',
                'Change a detail, re-generate everything.',
                'Every shot was generated independently, so changing an appearance means regenerating each one by hand — and eyeballing the results.',
              ],
              [
                'Induce memory graph',
                'Change a detail, the graph propagates it.',
                'Update a character node and the graph marks downstream shots stale. Induce re-generates only those, and re-verifies each as it lands.',
              ],
              [
                'Mid-story transitions',
                'The wardrobe change in act two actually happens.',
                'Narrative state changes are read from the script and applied as temporal events in the graph.',
              ],
              [
                'Retroactive revisions',
                'Rewrite page one. The cut reflects it.',
                'Upload a revised screenplay and Induce diffs the versions. Only changed scenes are re-broken-down, re-generated, and re-verified.',
              ],
            ].map(([kicker, title, body]) => (
              <div key={title} className="cap-item">
                <div className="kicker">{kicker}</div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
          <div className="highlight-box reveal section-stack">
            <div className="h-label">- The core principle</div>
            <p>
              <em>Induce holds the story together.</em> Continuity isn&apos;t a feature you turn on — it&apos;s
              the default from the moment you upload a script, checked on every shot.
            </p>
          </div>
        </div>
      </section>

      <PageFaqSection
        id="faq"
        eyebrow="- Frequently answered"
        title={
          <>
            Continuity, <em>answered.</em>
          </>
        }
        items={AI_VIDEO_CONTINUITY_FAQ}
      />

      <ProductMigrate {...SHOT_CONTROL_PIPELINE_MIGRATE} tone="dark" />

      <ProductFinalCta
        tone="light"
        eyebrow="- Stop chasing continuity"
        title={
          <>
            Hold it <em>by default.</em>
          </>
        }
        body="Upload a screenplay and your characters stay consistent to the last scene — no reference uploads, no manual setup, every shot verified."
        ghostLabel="Talk to Sales"
        ghostIsSales
        note="Free beta · No credit card · Continuity automatic on every project"
      />
    </div>
  );
}
